import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocalizedText } from '../../lib/localizedText'
import { exportRejectReasons } from './api/rejectReasonApi'
import Button from '../../components/ui/Button'
import ConfirmDialog, { confirmDialog } from '../../components/ui/ConfirmDialog'
import DataTable from '../../components/ui/DataTable'
import EmptyState from '../../components/ui/EmptyState'
import ExcelExportButton from '../../components/ui/ExcelExportButton'
import PageHeader from '../../components/ui/PageHeader'
import RefreshButton from '../../components/ui/RefreshButton'
import { useToast } from '../../components/ui/ToastProvider'
import { getApiErrorCode, isOptimisticLock } from '../../api/apiError'
import { rejectReasonColumns } from './components/RejectReasonColumns'
import RejectReasonFormDialog from './components/RejectReasonFormDialog'
import { useRejectReasons } from './hooks/useRejectReasons'
import type { RejectReason, RejectReasonUpsert } from './types'
import ReorderableLookupCards from '../../components/ui/ReorderableLookupCards'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
import './admin.css'

/**
 * Admin → Bilet Ret Gerekçesi yönetim ekranı (012). Liste (paylaşılan ui/DataTable) + ekle/düzenle
 * (FormDialog) + durum toggle + soft-delete (confirmDialog). Başlık = menü adı (`nav.admin.rejectReasons`).
 * Gerekçeler ret popup seçeneklerinin tek kaynağıdır; değişiklikler React Query invalidate ile yansır.
 * (011 TicketTypesAdminPage ile birebir desen.)
 */
/** "Diğer" sistem gerekçesi (id=-1) — sıralamada yerinde sabit (taşınamaz). */
const OTHER_ID = -1

export default function RejectReasonsAdminPage() {
  const { t } = useTranslation()
  const localized = useLocalizedText()
  const { list, create, update, changeStatus, remove, reorder } = useRejectReasons()
  const toast = useToast()

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<RejectReason | null>(null)
  const [nameError, setNameError] = useState<string | null>(null)

  const rows = list.data?.content ?? []
  const submitting = create.isPending || update.isPending
  const busyId = changeStatus.isPending
    ? (changeStatus.variables?.id ?? null)
    : remove.isPending
      ? (remove.variables ?? null)
      : null
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  const notify = (severity: 'success' | 'error', summary: string) =>
    toast.show({ severity, summary, life: 3000 })

  const openCreate = () => {
    setEditing(null)
    setNameError(null)
    setFormOpen(true)
  }
  const openEdit = (row: RejectReason) => {
    setEditing(row)
    setNameError(null)
    setFormOpen(true)
  }

  // Domaine özel ele alma ekranda kalır; optimistic-lock/generic toast'ı merkez (019) basar.
  const handleMutationError = (err: unknown) => {
    if (getApiErrorCode(err) === 'DUPLICATE_NAME') {
      setNameError(t('admin.rejectReasons.errors.duplicate'))
      return
    }
    if (isOptimisticLock(err)) {
      setFormOpen(false)
      list.refetch()
    }
  }

  const onSubmit = (payload: RejectReasonUpsert) => {
    if (editing) {
      update.mutate(
        { id: editing.id, body: { ...payload, modDate: editing.modDate } },
        {
          onSuccess: () => {
            notify('success', t('admin.rejectReasons.toast.updated'))
            setFormOpen(false)
          },
          onError: handleMutationError,
        },
      )
    } else {
      create.mutate(payload, {
        onSuccess: () => {
          notify('success', t('admin.rejectReasons.toast.created'))
          setFormOpen(false)
        },
        onError: handleMutationError,
      })
    }
  }

  const onToggle = (row: RejectReason, active: boolean) => {
    changeStatus.mutate(
      { id: row.id, body: { recordStatus: active ? 1 : 0, modDate: row.modDate } },
      {
        onSuccess: () => notify('success', t('admin.rejectReasons.toast.statusChanged')),
        // Hata (optimistic-lock/generic) → merkezî toast (019).
      },
    )
  }

  // Satırı yukarı/aşağı taşı → görünen sırayı komşuyla takas edip tüm id sırasını backend'e yaz (1..N).
  // "Diğer" (-1) korunur: ne kendisi ne de komşusuyla takası yapılır (yerinde sabit).
  const onMove = (row: RejectReason, dir: 'up' | 'down') => {
    const idx = rows.findIndex((r) => r.id === row.id)
    const swapWith = dir === 'up' ? idx - 1 : idx + 1
    if (idx < 0 || swapWith < 0 || swapWith >= rows.length) return
    if (row.id === OTHER_ID || rows[swapWith].id === OTHER_ID) return
    const orderedIds = rows.map((r) => r.id)
    ;[orderedIds[idx], orderedIds[swapWith]] = [orderedIds[swapWith], orderedIds[idx]]
    reorder.mutate(orderedIds, {
      onError: () => notify('error', t('common.reorder.error')),
    })
  }

  const onDelete = (row: RejectReason) => {
    confirmDialog({
      header: t('admin.rejectReasons.delete.title'),
      message: t('admin.rejectReasons.delete.confirm', { name: row.nameTr }),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('admin.rejectReasons.delete.accept'),
      accept: () =>
        remove.mutate(row.id, {
          onSuccess: () => notify('success', t('admin.rejectReasons.toast.deleted')),
          // Hata → merkezî toast (019).
        }),
    })
  }

  return (
    <div className="admin-page">
      <ConfirmDialog />
      <RejectReasonFormDialog
        visible={formOpen}
        initial={editing}
        submitting={submitting}
        nameError={nameError}
        onClearNameError={() => setNameError(null)}
        onSubmit={onSubmit}
        onHide={() => setFormOpen(false)}
      />

      <PageHeader
        title={t('nav.admin.rejectReasons')}
        actions={
          <>
            <RefreshButton onRefresh={() => list.refetch()} />
            <ExcelExportButton onExport={exportRejectReasons} disabled={rows.length === 0} />
            <Button icon="pi pi-plus" label={t('admin.rejectReasons.actions.new')} onClick={openCreate} />
          </>
        }
      />

      {/* Sıralanabilir (SORT_INDEX) liste → sayfalama kapalı, kolon sıralaması yok; daima sıra düzeninde. */}
      {isMobile ? (
        <ReorderableLookupCards
          items={rows}
          loading={list.isLoading}
          busyId={busyId}
          reordering={reorder.isPending}
          onMove={onMove}
          onEdit={openEdit}
          onToggle={onToggle}
          onDelete={onDelete}
          isProtected={(row) => row.id === OTHER_ID}
          emptyTitle={t('admin.rejectReasons.empty.title')}
          emptyDescription={t('admin.rejectReasons.empty.desc')}
          labels={{
            active: t('admin.rejectReasons.status.active'),
            passive: t('admin.rejectReasons.status.passive'),
            edit: t('admin.rejectReasons.actions.edit'),
            delete: t('admin.rejectReasons.actions.delete'),
            toggle: t('admin.rejectReasons.actions.toggle'),
            moveUp: t('common.reorder.up'),
            moveDown: t('common.reorder.down'),
            menu: t('common.actionsMenu'),
          }}
        />
      ) : (
      <DataTable
        value={rows}
        loading={list.isLoading}
        paginator={false}
        emptyMessage={
          <EmptyState
            title={t('admin.rejectReasons.empty.title')}
            description={t('admin.rejectReasons.empty.desc')}
          />
        }
      >
        {rejectReasonColumns({
          t,
          localized,
          busyId,
          rows,
          reordering: reorder.isPending,
          onMove,
          onEdit: openEdit,
          onToggle,
          onDelete,
        })}
      </DataTable>
      )}
    </div>
  )
}
